/**
 * KsefInvoices — lista faktur odebranych z KSeF + podgląd (Faza 2c).
 *
 * Filtry (kierunek/status/szukaj), tabela, drawer podglądu (oficjalny PDF z generatora MF, XML),
 * zmiana statusu, „Synchronizuj" (odpala odbiór dla aktywnych połączeń widocznych firm).
 * Backend: /api/ksef/invoices (+ [id]), /api/ksef/connections/[id]/sync.
 *
 * i18n: tabela (nagłówki kolumn, wartości statusu/kierunku w komórkach) + pasek filtrów
 * (kierunek/status/szukaj + zakres dat synchronizacji) przez `t()` — klucze pod
 * `tenant.ksefInvoices.*`. Nagłówek strony (tytuł/podtytuł), przycisk „Synchronizuj",
 * komunikaty toast i drawer podglądu ZOSTAJĄ inline PL — pre-existing dług, osobny task
 * (nie dotykaj przy okazji innych zmian w tym pliku, żeby nie rozmywać zakresu PR).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Download, ExternalLink, FileCheck, FilePlus, Receipt, RefreshCw } from 'lucide-react';

import { Button } from '@/components/Button';
import {
  type ColumnDefinition,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
} from '@/components/configurable-table';
import { DatePicker } from '@/components/DatePicker';
import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { CreateRecordFromKsefModal } from '@/components/ksef/CreateRecordFromKsefModal';
import { KsefPdfDrawer } from '@/components/ksef/KsefPdfDrawer';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import {
  buildCompanyColumn,
  type ExtRefLabels,
  useExtRefLabelsGetter,
} from '@/lib/ext-list-columns';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';

interface KsefInvoiceListItem {
  id: string;
  companyId: string;
  ksefNumber: string;
  invoiceNumber: string | null;
  invoiceType: string | null;
  direction: string;
  sellerNip: string | null;
  sellerName: string | null;
  buyerNip: string | null;
  buyerName: string | null;
  issueDate: string | null;
  netAmount: string | null;
  vatAmount: string | null;
  grossAmount: string | null;
  currency: string | null;
  status: string;
}

/** Detal z GET /api/ksef/invoices/:id — tylko pola, których używa drawer. */
interface KsefInvoiceDetailDto extends KsefInvoiceListItem {
  xmlData: string | null;
  ksefAcquisitionDate: string | null;
  linkedRecordId: string | null;
  linkedModuleId: string | null;
  /** Link weryfikacyjny KSeF (kod QR na PDF) — dokłada go API obok wiersza faktury. */
  verificationUrl: string | null;
}

// Etykieta statusu — `t()` dynamiczny (klucz budowany ze statusu, mirror wzorca
// `tenant.saleInvoices.invoiceKindValues.${value}` w `SaleInvoices.tsx`), klucze w
// `tenant.ksefInvoices.status.*` — używana w komórce statusu tabeli (`columns` niżej) i
// w filtrze statusu nad tabelą. Drawer podglądu ma WŁASNY, pre-existing inline `<select>`
// z tymi samymi wartościami — świadomie nietykany (osobny dług, patrz komentarz nagłówkowy).
const STATUS_CLS: Record<string, string> = {
  new: 'bg-accent-100 text-accent-700 dark:bg-accent-900/30',
  verified: 'bg-warning-100 text-warning-700 dark:bg-warning-900/30',
  booked: 'bg-success-100 text-success-700 dark:bg-success-900/30',
  rejected: 'bg-danger-100 text-danger-700 dark:bg-danger-900/30',
};

const money = (v: string | null, cur: string | null) =>
  v == null
    ? '—'
    : `${Number(v).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} ${cur ?? 'PLN'}`;

const inputCls =
  'rounded-md border border-border-primary bg-bg-primary px-2.5 py-1.5 text-sm text-text-primary';

/**
 * Serializuje `ColumnHeaderFilters` (kolumnId → filtr, stan UI) do `?headerFilters=`
 * (JSON.stringify HeaderFilter[]) — mirror `Users.tsx`/`SaleInvoices.tsx`. KLUCZOWE:
 * `field` w wyjściowym obiekcie MUSI być `headerFilter.fieldSlug` kolumny, NIE
 * `columnId` z UI — kontrakt z whitelistą `HEADER_FILTER_FIELDS` w
 * `apps/api/app/api/ksef/invoices/route.ts` operuje na backendowych slugach.
 * Wyekstrahowane jako czysta funkcja (Finding [C] pre-commit review) — w tym pliku
 * KAŻDA filtrowalna kolumna ma `fieldSlug === id`, więc regresja „użyto columnId
 * zamiast fieldSlug" byłaby niewidoczna w teście renderującym prawdziwe kolumny strony;
 * czysta funkcja pozwala przetestować przypadek slug ≠ id bez sztucznych kolumn
 * w prawdziwym renderze (patrz `KsefInvoices.test.tsx`). Generyczna po `T` (nie
 * zawężona do `KsefInvoiceListItem`) — testowalna z synthetycznym `ColumnDefinition`
 * bez importowania interfejsu wiersza tej strony.
 */
export function serializeHeaderFiltersForRequest<T>(
  headerFilters: ColumnHeaderFilters,
  columns: ColumnDefinition<T>[],
): string | undefined {
  const merged: ColumnHeaderFilter[] = [];
  for (const [columnId, filter] of Object.entries(headerFilters)) {
    const col = columns.find((c) => c.id === columnId);
    const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
    if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
  }
  return merged.length > 0 ? JSON.stringify(merged) : undefined;
}

export function KsefInvoices() {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  /** Otwiera rekord utworzony z faktury: wbudowana faktura (wg kierunku) lub rekord modułu. */
  const openLinkedRecord = (d: KsefInvoiceDetailDto) => {
    if (!d.linkedRecordId) return;
    const url = d.linkedModuleId
      ? `/modules/${d.linkedModuleId}/${d.linkedRecordId}`
      : `/${d.direction === 'sales' ? 'sale-invoices' : 'purchase-invoices'}/${d.linkedRecordId}`;
    setSelectedId(null);
    navigate(url);
  };

  const [direction, setDirection] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Zaznaczanie wierszy — checkboxy gotowe pod przyszłe akcje zbiorcze (ogólny silnik
  // akcji kartotek). Na razie sama selekcja, bez paska akcji i bez endpointu bulk.
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [showXml, setShowXml] = useState(false);
  const [showCreateRecord, setShowCreateRecord] = useState(false);
  const [syncFrom, setSyncFrom] = useState('');
  const [syncTo, setSyncTo] = useState('');

  // Filtry per kolumna z popupu w nagłówku (Kreator Listy) — server-side, patrz
  // `serializedHeaderFilters` niżej. Kierunek/status/szukaj zostają OSOBNYMI kontrolkami
  // nad tabelą (niezmienione) — nie są foldowane w wiersz nagłówka.
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});

  // Czyść selekcję przy zmianie kryteriów listy — inaczej `selectedRowIds` mogłoby wskazywać
  // wiersze spoza aktualnie pobranej strony (pułapka „duchów", gdy dojdzie pasek akcji zbiorczych).
  useEffect(() => {
    setSelectedRowIds([]);
  }, [direction, status, search, headerFilters]);

  // Model B (widoczność per-firma) — tryb "Wszystkie firmy" omija ?companyId=, tryb
  // konkretnej firmy dokleja go do fetchu i klucza zapytania (§4.8 CLAUDE.md).
  const scope = useActiveCompanyScope();
  const companyId = companyIdParam(scope);
  const { me } = useTenantAuth();
  const companies = useMemo(() => me?.companies ?? [], [me?.companies]);

  // Getter stabilny (patrz `useExtRefLabelsGetter`) — dziś zasila WYŁĄCZNIE etykietę
  // kolumny „Firma" (KSeF nie ma katalogu pól ext), aktualizowany niżej PO `listQuery`.
  const [getRefLabels, setRefLabels] = useExtRefLabelsGetter();

  const columns = useMemo<ColumnDefinition<KsefInvoiceListItem>[]>(
    () => [
      {
        id: 'ksef_number',
        accessor: 'ksefNumber',
        header: t('tenant.ksefInvoices.columns.ksefNumber'),
        defaultWidth: 180,
        sortable: false,
        headerFilter: { category: 'text', fieldSlug: 'ksef_number' },
        renderCell: (value) => (
          <span className="font-mono text-xs text-text-primary">{String(value)}</span>
        ),
      },
      buildCompanyColumn<KsefInvoiceListItem>({ companies, getRefLabels, t }),
      {
        id: 'invoice_number',
        accessor: 'invoiceNumber',
        header: t('tenant.ksefInvoices.columns.invoiceNumber'),
        defaultWidth: 160,
        sortable: false,
        headerFilter: { category: 'text', fieldSlug: 'invoice_number' },
        renderCell: (value) => (
          <span className="text-text-primary">{value ? String(value) : '—'}</span>
        ),
      },
      {
        id: 'direction',
        accessor: 'direction',
        header: t('tenant.ksefInvoices.columns.direction'),
        defaultWidth: 110,
        sortable: false,
        renderCell: (value) => (
          <span>
            {value === 'sales'
              ? t('tenant.ksefInvoices.direction.sales')
              : t('tenant.ksefInvoices.direction.purchase')}
          </span>
        ),
      },
      {
        id: 'counterparty',
        accessor: (row) =>
          row.direction === 'sales'
            ? (row.buyerName ?? row.buyerNip)
            : (row.sellerName ?? row.sellerNip),
        header: t('tenant.ksefInvoices.columns.counterparty'),
        defaultWidth: 220,
        sortable: false,
        renderCell: (_value, row) => {
          const counterparty =
            row.direction === 'sales'
              ? (row.buyerName ?? row.buyerNip)
              : (row.sellerName ?? row.sellerNip);
          return <span className="text-text-primary">{counterparty ?? '—'}</span>;
        },
      },
      {
        id: 'issue_date',
        accessor: 'issueDate',
        header: t('tenant.ksefInvoices.columns.issueDate'),
        defaultWidth: 120,
        sortable: false,
        headerFilter: { category: 'date', fieldSlug: 'issue_date' },
        renderCell: (value) => (
          <span className="text-text-secondary">{value ? String(value) : '—'}</span>
        ),
      },
      {
        id: 'gross_amount',
        accessor: 'grossAmount',
        header: t('tenant.ksefInvoices.columns.grossAmount'),
        defaultWidth: 140,
        align: 'right',
        sortable: false,
        dataType: 'number',
        headerFilter: { category: 'number', fieldSlug: 'gross_amount' },
        renderCell: (_value, row) => (
          <span className="text-text-primary">{money(row.grossAmount, row.currency)}</span>
        ),
      },
      {
        id: 'status',
        accessor: 'status',
        header: t('tenant.ksefInvoices.columns.status'),
        defaultWidth: 130,
        sortable: false,
        renderCell: (_value, row) => (
          <span
            className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${STATUS_CLS[row.status] ?? ''}`}
          >
            {t(`tenant.ksefInvoices.status.${row.status}`, { defaultValue: row.status })}
          </span>
        ),
      },
    ],
    [t, companies, getRefLabels],
  );

  const serializedHeaderFilters = useMemo(
    () => serializeHeaderFiltersForRequest(headerFilters, columns),
    [headerFilters, columns],
  );

  const listKey = [
    'ksef-invoices',
    {
      direction,
      status,
      search,
      companyId: companyId ?? 'all',
      headerFilters: serializedHeaderFilters,
    },
  ] as const;
  const listQuery = useQuery({
    queryKey: listKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (direction) params.set('direction', direction);
      if (status) params.set('status', status);
      if (search.trim()) params.set('search', search.trim());
      if (companyId) params.set('companyId', companyId);
      if (serializedHeaderFilters) params.set('headerFilters', serializedHeaderFilters);
      const res = await api.get<{ invoices: KsefInvoiceListItem[]; extRefLabels?: ExtRefLabels }>(
        `/ksef/invoices?${params.toString()}`,
      );
      return res.data;
    },
  });
  setRefLabels(listQuery.data?.extRefLabels);
  const invoices = listQuery.data?.invoices ?? [];

  const detailQuery = useQuery({
    queryKey: ['ksef-invoice', selectedId],
    enabled: selectedId !== null,
    queryFn: async () => {
      const res = await api.get<{
        invoice: Omit<KsefInvoiceDetailDto, 'verificationUrl'>;
        verificationUrl?: string | null;
      }>(`/ksef/invoices/${selectedId}`);
      return { ...res.data.invoice, verificationUrl: res.data.verificationUrl ?? null };
    },
  });
  const detail = detailQuery.data;

  const syncMutation = useMutation({
    mutationFn: async () => {
      const conns = (
        await api.get<{ connections: { id: string; isActive: boolean }[] }>('/ksef/connections')
      ).data.connections;
      const active = conns.filter((c) => c.isActive);
      if (active.length === 0) throw new Error('NO_ACTIVE_CONNECTION');
      const dateBody: { dateFrom?: string; dateTo?: string } = {};
      if (syncFrom) dateBody.dateFrom = syncFrom;
      if (syncTo) dateBody.dateTo = syncTo;
      let created = 0;
      for (const c of active) {
        const r = await api.post<{ invoicesNew: number }>(
          `/ksef/connections/${c.id}/sync`,
          dateBody,
        );
        created += r.data.invoicesNew ?? 0;
      }
      return created;
    },
    onSuccess: (created) => {
      toast.show({
        title: `Synchronizacja zakończona — nowych faktur: ${created}`,
        variant: 'success',
      });
      qc.invalidateQueries({ queryKey: ['ksef-invoices'] });
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'NO_ACTIVE_CONNECTION') {
        toast.show({
          title:
            'Brak aktywnego połączenia KSeF — skonfiguruj je w Ustawieniach → Integracje → KSeF.',
          variant: 'error',
        });
        return;
      }
      // Uwidocznij realny komunikat z KSeF (pole errorMessage w odpowiedzi 502).
      const ksefMsg = isAxiosError(err)
        ? (err.response?.data?.errorMessage ?? err.response?.data?.message)
        : undefined;
      toast.show({
        title: ksefMsg
          ? `Synchronizacja nie powiodła się: ${ksefMsg}`
          : 'Synchronizacja nie powiodła się.',
        variant: 'error',
      });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async (input: { id: string; status: string }) =>
      api.patch(`/ksef/invoices/${input.id}`, { status: input.status }),
    onSuccess: () => {
      toast.show({ title: 'Zmieniono status', variant: 'success' });
      qc.invalidateQueries({ queryKey: ['ksef-invoices'] });
      if (selectedId) qc.invalidateQueries({ queryKey: ['ksef-invoice', selectedId] });
    },
    onError: () => toast.show({ title: 'Nie udało się zmienić statusu', variant: 'error' }),
  });

  const downloadXml = () => {
    if (!detail?.xmlData) return;
    const blob = new Blob([detail.xmlData], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ksef-${detail.ksefNumber}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-text-primary">{t('tenant.ksefInvoices.title')}</h1>
          <p className="mt-1 text-sm text-text-tertiary">{t('tenant.ksefInvoices.subtitle')}</p>
        </div>
        <div className="flex items-end gap-2">
          <label className="flex flex-col text-[11px] text-text-secondary">
            {t('tenant.ksefInvoices.filters.dateFrom')}
            <DatePicker value={syncFrom || ''} onChange={setSyncFrom} className={inputCls} />
          </label>
          <label className="flex flex-col text-[11px] text-text-secondary">
            {t('tenant.ksefInvoices.filters.dateTo')}
            <DatePicker value={syncTo || ''} onChange={setSyncTo} className={inputCls} />
          </label>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<RefreshCw className="h-4 w-4" />}
            loading={syncMutation.isPending}
            onClick={() => syncMutation.mutate()}
          >
            {t('tenant.ksefInvoices.syncButton')}
          </Button>
        </div>
      </div>

      {/* Filtry */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Dropdown
          value={direction}
          onChange={setDirection}
          className={inputCls}
          options={[
            { value: '', label: t('tenant.ksefInvoices.filters.directionAll') },
            { value: 'purchase', label: t('tenant.ksefInvoices.direction.purchase') },
            { value: 'sales', label: t('tenant.ksefInvoices.direction.sales') },
          ]}
        />
        <Dropdown
          value={status}
          onChange={setStatus}
          className={inputCls}
          options={[
            { value: '', label: t('tenant.ksefInvoices.filters.statusAll') },
            { value: 'new', label: t('tenant.ksefInvoices.status.new') },
            { value: 'verified', label: t('tenant.ksefInvoices.status.verified') },
            { value: 'booked', label: t('tenant.ksefInvoices.status.booked') },
            { value: 'rejected', label: t('tenant.ksefInvoices.status.rejected') },
          ]}
        />
        <Input
          type="text"
          placeholder={t('tenant.ksefInvoices.filters.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          containerClassName="min-w-64 flex-1"
        />
      </div>

      {/* Tabela — Kreator Listy (ConfigurableTable): filtry per kolumna server-side przez
          `headerFilters` (kontrolowane, patrz `serializedHeaderFilters` wyżej — listing ładuje
          ≤200 wierszy bez paginacji, więc filtr MUSI iść na backend, nie po pobranej stronie).
          Wiersz filtrów renderuje kontrolki typowane (menu operatorów + edytor) dla kolumn z
          `headerFilter`; kolumny bez niego (kierunek/kontrahent/status mają własne kontrolki nad
          tabelą) NIE dostają pola — ekran nie kontroluje `columnFilters`, więc `FilterRowCell`
          renderuje pusty placeholder zamiast lokalnego filtra pobranej strony.
          `persistConfig={false}` — brak zapisu layoutu (mirror `AdminUsers.tsx`). */}
      <ConfigurableTable<KsefInvoiceListItem>
        tableId="ksef_invoices"
        testIdPrefix="ksef-invoices"
        columns={columns}
        data={invoices}
        isLoading={listQuery.isLoading}
        getRowId={(row) => row.id}
        selectable
        selectedIds={selectedRowIds}
        onSelectionChange={setSelectedRowIds}
        headerFilters={headerFilters}
        onHeaderFiltersChange={setHeaderFilters}
        persistConfig={false}
        showViewModeToggle={false}
        onRowClick={(row) => {
          setSelectedId(row.id);
          setShowXml(false);
        }}
        emptyIcon={Receipt}
        emptyTitle={t('tenant.ksefInvoices.empty.title')}
        emptyDescription={t('tenant.ksefInvoices.empty.description')}
      />

      {/* Drawer podglądu — oficjalny PDF z generatora MF + status/rekord/XML */}
      <KsefPdfDrawer
        open={selectedId !== null}
        onClose={() => setSelectedId(null)}
        source={
          detail?.xmlData
            ? {
                xml: detail.xmlData,
                ksefNumber: detail.ksefNumber,
                verificationUrl: detail.verificationUrl,
                acquisitionDate: detail.ksefAcquisitionDate ?? null,
              }
            : null
        }
        sourceState={
          detailQuery.isError ? 'error' : detail && !detail.xmlData ? 'noXml' : 'loading'
        }
        fileBaseName={detail?.invoiceNumber ?? detail?.ksefNumber}
        header={
          detail && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-bg-tertiary px-1.5 py-0.5 text-xs">
                  {detail.invoiceType ?? 'FA'}
                </span>
                <Dropdown
                  value={detail.status}
                  onChange={(v) => statusMutation.mutate({ id: detail.id, status: v })}
                  className={`${inputCls} ml-auto`}
                  options={[
                    { value: 'new', label: t('tenant.ksefInvoices.status.new') },
                    { value: 'verified', label: t('tenant.ksefInvoices.status.verified') },
                    { value: 'booked', label: t('tenant.ksefInvoices.status.booked') },
                    { value: 'rejected', label: t('tenant.ksefInvoices.status.rejected') },
                  ]}
                />
              </div>

              {/* Dekretacja — utworzenie rekordu / otwarcie utworzonego */}
              {detail.linkedRecordId ? (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded bg-success-100 px-3 py-2 text-xs text-success-700 dark:bg-success-900/30">
                  <span className="flex items-center gap-2">
                    <FileCheck className="h-4 w-4" />
                    {t('tenant.ksefInvoices.drawer.linked')}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<ExternalLink className="h-4 w-4" />}
                    onClick={() => openLinkedRecord(detail)}
                    data-testid="ksef-open-record-btn"
                  >
                    {detail.linkedModuleId
                      ? t('tenant.ksefInvoices.drawer.openRecord')
                      : detail.direction === 'sales'
                        ? t('tenant.ksefInvoices.drawer.openSaleInvoice')
                        : t('tenant.ksefInvoices.drawer.openPurchaseInvoice')}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<FilePlus className="h-4 w-4" />}
                  onClick={() => setShowCreateRecord(true)}
                  data-testid="ksef-create-record-btn"
                >
                  {t('tenant.ksefInvoices.drawer.createRecord')}
                </Button>
              )}
            </div>
          )
        }
      >
        {detail && (
          <>
            <div className="flex items-center gap-2 border-t border-border-primary pt-3">
              <Button variant="secondary" size="sm" onClick={() => setShowXml((v) => !v)}>
                {showXml
                  ? t('tenant.ksefInvoices.drawer.hideXml')
                  : t('tenant.ksefInvoices.drawer.showXml')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Download className="h-4 w-4" />}
                disabled={!detail.xmlData}
                onClick={downloadXml}
              >
                {t('tenant.ksefInvoices.drawer.downloadXml')}
              </Button>
            </div>
            {showXml && (
              <pre className="max-h-72 overflow-auto rounded bg-bg-primary p-2 text-[11px] text-text-secondary">
                {detail.xmlData ?? t('tenant.ksefInvoices.drawer.noXml')}
              </pre>
            )}
          </>
        )}
      </KsefPdfDrawer>

      {showCreateRecord && detail && (
        <CreateRecordFromKsefModal
          invoiceId={detail.id}
          invoiceLabel={`${detail.ksefNumber}${detail.invoiceNumber ? ` · ${detail.invoiceNumber}` : ''}`}
          direction={detail.direction}
          onClose={() => setShowCreateRecord(false)}
          onCreated={(recordRoute) => {
            setShowCreateRecord(false);
            // Od razu otwórz utworzony rekord (wcześniej trzeba było odświeżyć listę).
            if (recordRoute) {
              setSelectedId(null);
              navigate(recordRoute);
            }
          }}
        />
      )}
    </div>
  );
}

export default KsefInvoices;
